import { useSyncExternalStore } from 'react';

export interface ApiUser {
  id: string;
  kind: 'guest' | 'registered';
  displayName: string;
  username: string | null;
  role: 'player' | 'admin';
  uiLang: string;
  isDemo: boolean;
  createdAt: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, details?: unknown) {
    super(code);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Offset between the server clock and ours (server = Date.now() + offset), from API round trips. */
let clockOffset = 0;
let clockSamples = 0;

export function serverNow(): number {
  return Date.now() + clockOffset;
}

function recordServerTime(serverTime: number, sentAt: number, receivedAt: number): void {
  const estimate = serverTime - (sentAt + receivedAt) / 2;
  // Keep the sample with the lowest latency-weighted error: simple exponential smoothing.
  clockOffset = clockSamples === 0 ? estimate : clockOffset * 0.7 + estimate * 0.3;
  clockSamples++;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const sentAt = Date.now();
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  const receivedAt = Date.now();
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  const st = (json as { serverNow?: number } | null)?.serverNow;
  if (typeof st === 'number') recordServerTime(st, sentAt, receivedAt);
  if (!res.ok) {
    const j = json as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, j?.error ?? `http_${res.status}`, j?.details);
  }
  return json as T;
}

// ------------------------------------------------------------------ session store

let me: ApiUser | null = null;
let loaded = false;
let online = true;
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

export function useMe(): { user: ApiUser | null; loaded: boolean; online: boolean } {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => snapshot,
  );
}
let snapshot: { user: ApiUser | null; loaded: boolean; online: boolean } = {
  user: me,
  loaded,
  online,
};
function setMe(u: ApiUser | null, isOnline = true): void {
  me = u;
  loaded = true;
  online = isOnline;
  snapshot = { user: me, loaded, online };
  emit();
}

export async function refreshMe(): Promise<ApiUser | null> {
  try {
    const r = await api<{ user: ApiUser | null }>('GET', '/api/me');
    setMe(r.user);
    return r.user;
  } catch {
    setMe(null, false);
    return null;
  }
}

/** Returns the current user, creating a guest session on first online use. */
export async function ensureSession(uiLang?: string): Promise<ApiUser> {
  if (me) return me;
  const current = await refreshMe();
  if (current) return current;
  const r = await api<{ user: ApiUser }>('POST', '/api/auth/guest', { uiLang });
  setMe(r.user);
  return r.user;
}

export async function register(
  username: string,
  password: string,
  email?: string,
): Promise<ApiUser> {
  const r = await api<{ user: ApiUser }>('POST', '/api/auth/register', {
    username,
    password,
    email: email || undefined,
  });
  setMe(r.user);
  return r.user;
}

export async function login(username: string, password: string): Promise<ApiUser> {
  const r = await api<{ user: ApiUser }>('POST', '/api/auth/login', { username, password });
  setMe(r.user);
  return r.user;
}

export async function logout(): Promise<void> {
  await api('POST', '/api/auth/logout', {});
  setMe(null);
}

export function newEventId(): string {
  return crypto.randomUUID();
}
